import { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Route } from 'react-router';
import { findUsers, impersonateUser } from '../../store/authentication/actions';
import SelectionListBar from '../navigation/SelectionListBar';
import SearchBar from '../search/SearchBar';
import { PageHeader } from './PageHeader';
import { ScrollHeader } from './ScrollHeader';
import { useLibraryManagedProductGroupsFeaturePages } from '../../yourborrowbox/marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { getCatalogueSearchScopes, getYourBorrowBoxSearchScopes } from '../search/utils/mappers';
import toLower from 'lodash/toLower';
import { useTranslation } from 'react-i18next';
import { invalidateCartSummaryQuery, useCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { COLLECTION_NAVIGATION_V2, MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';
import { useSelectionListBar } from '../selection-list/SelectionListBarContext';
import { useStickyHeader } from './useStickyHeader';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { Routes } from 'react-router-dom';

export const HeaderContainer: FC = () => {
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const authentication = useAuthentication();
    const { isSticky } = useStickyHeader();
    const headerContainerRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [openSearchBar, setOpenSearchBar] = useState(false);

    function onOpenSearchBar() {
        setOpenSearchBar(true);
    }

    function onCloseSearchBar() {
        setOpenSearchBar(false);
    }

    function onSearchQueryChange(newQuery: string) {
        setSearchQuery(newQuery);
    }

    const { addToList } = useAddToList({
        callback: targetListId => {
            invalidateSelectionListAggregations(targetListId).then();
            invalidateSelectionListSummary(targetListId).then();
            invalidateSelectionListProducts(targetListId).then();
            invalidateSelectionListGroupedItems(targetListId).then();
        }
    });
    const { addToCart } = useAddToCart({
        callback: () => {
            invalidateCartAggregations().then();
            invalidateShoppingCartProducts().then();
            invalidateCartGroupedItems().then();
            invalidateCartSummaryQuery().then();
        }
    });

    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const { selectionListBarExpanded } = useSelectionListBar();
    const headerClasses = classNames('header', {
        'with-sl-bar-exp': selectionListBarExpanded
    });

    const shopSearchBar = (
        <SearchBar
            searchBarActive={openSearchBar}
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            onCloseSearchBar={onCloseSearchBar}
            onOpenSearchBar={onOpenSearchBar}
            onAddToCart={addToCart}
            onAddToList={addToList}
            searchScopes={getCatalogueSearchScopes(isMultiLanguageShopEnabled)}
            searchLabel={t('components.searchBar.shopSearch')}
            getSearchPath={() => '/search'}
            isInYourBorrowBox={false}
        />
    );
    const yourBorrowboxSearchBar = (
        <SearchBar
            searchBarActive={openSearchBar}
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            onCloseSearchBar={onCloseSearchBar}
            onOpenSearchBar={onOpenSearchBar}
            onAddToCart={addToCart}
            onAddToList={addToList}
            searchScopes={getYourBorrowBoxSearchScopes()}
            searchLabel={t('components.searchBar.collectionSearch')}
            getSearchPath={scopeKey =>
                isCollectionNavigationV2Enabled
                    ? `/collectionmgmt/search/${toLower(scopeKey)}`
                    : `/collection/search/${toLower(scopeKey)}`
            }
            isInYourBorrowBox={true}
        />
    );

    const searchBar = (
        <Routes>
            <Route path="/shop/:shopLanguage?/:loanFormat?/*" element={shopSearchBar} />
            <Route path="/catalogue/:loanFormat?/*" element={shopSearchBar} />
            <Route path="/search/*" element={shopSearchBar} />
            <Route path="/unlimited/*" element={shopSearchBar} />
            <Route path="/cart/*" element={shopSearchBar} />
            <Route path="/checkout/*" element={shopSearchBar} />
            <Route path="/assistant/*" element={shopSearchBar} />
            <Route path="/bundles/*" element={shopSearchBar} />
            <Route path="/orderlists/*" element={shopSearchBar} />
            <Route path="/language/*" element={shopSearchBar} />
            <Route path="/libxchange/*" element={shopSearchBar} />
            <Route path="/products/*" element={shopSearchBar} />
            <Route path="/collection/*" element={yourBorrowboxSearchBar} />
            <Route path="/localcontent/*" element={yourBorrowboxSearchBar} />
            <Route path="/marketing/*" element={yourBorrowboxSearchBar} />
            <Route path="/collectionmgmt/*" element={yourBorrowboxSearchBar} />
            <Route path="/merchandising/*" element={yourBorrowboxSearchBar} />
        </Routes>
    );

    useLibraryManagedProductGroupsFeaturePages();
    const cartSummaryQuery = useCartSummaryQuery();

    return (
        <div ref={headerContainerRef} className={headerClasses} data-displayname="HeaderContainer">
            <PageHeader
                authentication={authentication}
                searchbar={!isSticky ? searchBar : null}
                impersonate={user => dispatch(impersonateUser(user))}
                findUsers={(searchString?: string) => dispatch(findUsers(searchString))}
                cartSummaryQuery={cartSummaryQuery}
            />
            {isSticky ? (
                <ScrollHeader
                    show={isSticky}
                    searchbar={searchBar}
                    onOpenSearchBar={onOpenSearchBar}
                    spoc={authentication.spoc}
                    cartSummaryQuery={cartSummaryQuery}
                />
            ) : null}
            <SelectionListBar />
        </div>
    );
};
